// UUID builder & order 推断：从 StationAddStepDevice.vue 抽出
// - Builder 首参统一为 stationId（原实现直接读 props.stationId，抽出后由调用方传入）
// - stationId 为空时返回空字符串（保持与原实现一致的行为）
// - Order 推断纯函数（不依赖 stationId）

import {
  parsePositiveOrder,
  nextOrderFromList,
  inferEblockSeq,
} from './helpers'

/** 将 stationId 归一化为字符串，去除首尾空白 */
function trimStationId(stationId) {
  return String(stationId ?? '').trim()
}

// ============================================================
// UUID builder（均依赖 stationId）
// ============================================================

/** eLink：S{stationId}_ELK{order} */
export function buildElinkUuid(stationId, order) {
  const sid = trimStationId(stationId)
  const elinkOrder = Math.max(0, Number(order) || 0)
  if (!sid) return ''
  return `S${sid}_ELK${elinkOrder}`
}

/** Galaxy：S{stationId}_GLX{order}（集中式电站独有） */
export function buildGalaxyUuid(stationId, order) {
  const sid = trimStationId(stationId)
  const galaxyOrder = Math.max(1, Number(order) || 1)
  if (!sid) return ''
  return `S${sid}_GLX${galaxyOrder}`
}

/** eBlock：S{stationId}_ELK{elinkOrder}_EB{eblockOrder} */
export function buildEblockUuid(stationId, elinkOrder, eblockOrder) {
  const sid = trimStationId(stationId)
  const normalizedElinkOrder = Math.max(0, Number(elinkOrder) || 0)
  const normalizedEblockOrder = Math.max(1, Number(eblockOrder) || 1)
  if (!sid) return ''
  return `S${sid}_ELK${normalizedElinkOrder}_EB${normalizedEblockOrder}`
}

/** eBlock 电池簇：S{stationId}_ELK{elinkOrder}_EB{eblockOrder}_BC{clusterOrder} */
export function buildEblockBatteryClusterUuid(stationId, elinkOrder, eblockOrder, clusterOrder) {
  const sid = trimStationId(stationId)
  const normalizedElinkOrder = Math.max(0, Number(elinkOrder) || 0)
  const normalizedEblockOrder = Math.max(1, Number(eblockOrder) || 1)
  const normalizedClusterOrder = Math.max(1, Number(clusterOrder) || 1)
  if (!sid) return ''
  return `S${sid}_ELK${normalizedElinkOrder}_EB${normalizedEblockOrder}_BC${normalizedClusterOrder}`
}

/** eBlock 电池簇 devId：EBLOCK_{elinkOrder}_{eblockOrder}_BC{clusterOrder} */
export function buildEblockBatteryClusterDevId(elinkOrder, eblockOrder, clusterOrder) {
  const normalizedElinkOrder = Math.max(0, Number(elinkOrder) || 0)
  const normalizedEblockOrder = Math.max(1, Number(eblockOrder) || 1)
  const normalizedClusterOrder = Math.max(1, Number(clusterOrder) || 1)
  return `EBLOCK_${normalizedElinkOrder}_${normalizedEblockOrder}_BC${normalizedClusterOrder}`
}

/** eBlock 内嵌水机：S{stationId}_ELK{elinkOrder}_EB{eblockOrder}_WT{waterOrder} */
export function buildEblockWaterMachineUuid(
  stationId,
  elinkOrder,
  eblockOrder,
  waterOrder = eblockOrder,
) {
  const sid = trimStationId(stationId)
  const normalizedElinkOrder = Math.max(0, Number(elinkOrder) || 0)
  const normalizedEblockOrder = Math.max(1, Number(eblockOrder) || 1)
  const normalizedWaterOrder = Math.max(1, Number(waterOrder) || 1)
  if (!sid) return ''
  return `S${sid}_ELK${normalizedElinkOrder}_EB${normalizedEblockOrder}_WT${normalizedWaterOrder}`
}

/** eLink 顶层水机：S{stationId}_ELK{elinkOrder}_WT{waterOrder} */
export function buildElinkWaterMachineUuid(stationId, elinkOrder, waterOrder) {
  const sid = trimStationId(stationId)
  const normalizedElinkOrder = Math.max(0, Number(elinkOrder) || 0)
  const normalizedWaterOrder = Math.max(1, Number(waterOrder) || 1)
  if (!sid) return ''
  return `S${sid}_ELK${normalizedElinkOrder}_WT${normalizedWaterOrder}`
}

/** eLink 计量电表：S{stationId}_MEA{elinkOrder}_{meterOrder} */
export function buildElinkMeterUuid(stationId, elinkOrder, meterOrder) {
  const sid = trimStationId(stationId)
  const normalizedElinkOrder = Math.max(0, Number(elinkOrder) || 0)
  const normalizedMeterOrder = Math.max(1, Number(meterOrder) || 1)
  if (!sid) return ''
  return `S${sid}_MEA${normalizedElinkOrder}_${normalizedMeterOrder}`
}

/**
 * 顶层电表：S{stationId}_{code}{order}
 * 其中 code 由 meterUuidConfigMap[meterType] 决定（见 constants.js）
 */
export function buildMeterUuid(stationId, meterUuidConfigMap, meterType, order) {
  const config = meterUuidConfigMap[Number(meterType)]
  const sid = trimStationId(stationId)
  if (!config || !sid || !order) return ''
  return `S${sid}_${config.code}${order}`
}

/** 变压器：S{stationId}_TFM{order} */
export function buildTransformerUuid(stationId, order) {
  const sid = trimStationId(stationId)
  if (!sid) return ''
  return `S${sid}_TFM${order}`
}

/** 光伏：S{stationId}_PVD{order} */
export function buildPvUuid(stationId, order) {
  const sid = trimStationId(stationId)
  if (!sid) return ''
  return `S${sid}_PVD${order}`
}

/** 充电桩：S{stationId}_CP{order} */
export function buildChargePileUuid(stationId, order) {
  const sid = trimStationId(stationId)
  if (!sid) return ''
  return `S${sid}_CP${order}`
}

/**
 * 生成 eBlock 内嵌水机的完整元信息（名称/型号/UUID/devId）
 * elinkIdx 为 elink 的序号；seq 为水机在 eBlock 中的序号
 */
export function buildWaterMachineMeta(stationId, elinkIdx, seq, productModel = '') {
  const index = Math.max(1, Number(seq) || 1)
  const elinkOrder = Math.max(0, Number(elinkIdx) || 0)
  return {
    waterMachineName: `${index}#水机`,
    waterMachineModel: String(productModel ?? '').trim(),
    waterMachineUuid: buildEblockWaterMachineUuid(stationId, elinkOrder, index),
    waterMachineDevId: `WATER_${elinkOrder}_${index}`,
  }
}

// ============================================================
// Order 推断（不依赖 stationId，纯函数）
// ============================================================

/** 从 elink 项推断其序号：优先 badge，其次从 uuid 中提取 */
export function inferElinkOrder(item, fallback = 0) {
  const badge = Number(item?.badge)
  if (Number.isInteger(badge) && badge > 0) return badge
  return (
    parsePositiveOrder(item?.uuid, /^S\d+_ELK(\d+)(?:_|$)/i)
    || Math.max(0, Number(fallback) || 0)
  )
}

/** 从 eBlock 项推断其序号：优先按 name（`N-M#eBlock`），其次 uuid，再其次 deviceId */
export function inferEblockOrder(item, fallback = 1) {
  return (
    inferEblockSeq(item?.name, 0)
    || parsePositiveOrder(item?.uuid, /^S\d+_ELK\d+_EB(\d+)(?:_|$)/i)
    || parsePositiveOrder(item?.deviceId, /^EBLOCK_\d+_(\d+)$/i)
    || Math.max(1, Number(fallback) || 1)
  )
}

/** 从 eLink 顶层水机项推断其序号：优先按 name（`N#水机`），其次 uuid，再其次 devId */
export function inferElinkWaterMachineOrder(item, fallback = 1) {
  return (
    parsePositiveOrder(item?.name ?? item?.waterMachineName, /^(\d+)#水机$/)
    || parsePositiveOrder(item?.uuid ?? item?.waterMachineUuid, /^S\d+_ELK\d+_WT(\d+)$/i)
    || parsePositiveOrder(
      item?.deviceId ?? item?.waterMachineDevId ?? item?.devId,
      /^WATER_\d+_(\d+)$/i,
    )
    || Math.max(1, Number(fallback) || 1)
  )
}

/** 从 eLink 计量电表项推断其序号（从 uuid 尾部数字） */
export function inferElinkMeterOrder(item, fallback = 1) {
  return (
    parsePositiveOrder(item?.uuid, /^S\d+_MEA\d+_(\d+)$/i)
    || Math.max(1, Number(fallback) || 1)
  )
}

/** 返回下一个可用 eBlock 序号 */
export function nextEblockOrder(list) {
  return nextOrderFromList(list, (item, index) => inferEblockOrder(item, index + 1))
}

/** 返回下一个可用 eLink 顶层水机序号 */
export function nextElinkWaterMachineOrder(list) {
  return nextOrderFromList(list, (item, index) => inferElinkWaterMachineOrder(item, index + 1))
}

/** 返回下一个可用 eLink 计量电表序号 */
export function nextElinkMeterOrder(list) {
  return nextOrderFromList(list, (item, index) => inferElinkMeterOrder(item, index + 1))
}
